/**
 * Feature Access Control Utility
 * Uses ALLOW-based permissions to determine feature access by subscription tier
 * 
 * This is an explicit ALLOW model - features are only accessible if explicitly allowed for the tier.
 * If a feature is not listed for a tier, access is DENIED by default.
 */

import { getSubscriptionInfo } from './subscription';

/**
 * Feature access matrix - defines which features are allowed for each tier
 * ALLOW-based model: Only features listed here are accessible for each tier
 */
const FEATURE_ACCESS_MATRIX = {
  // Free tier - "Today" view only for major features
  free: [
    'dashboard',
    'clients',            // Restricted to today's appointments by subscription.js
    'calendar',           // Restricted to today by subscription.js
    'forms',
    'services',
    'financial',
    'users',
    'settings'
  ],

  // Pro tier (£99) - Full access with record limits
  pro: [
    'dashboard',
    'clients',
    'calendar',
    'forms',
    'services',
    'financial',
    'analytics',
    'reports',
    'leads',
    'activity',
    'marketing_dashboard',
    'automations',
    'audiences',
    'users',
    'settings',
    'client_docs_access'   // Explicit feature for docs
  ],

  // Enterprise tier (£350) - Unlimited
  enterprise: [
    'dashboard',
    'clients',
    'calendar',
    'forms',
    'services',
    'financial',
    'analytics',
    'reports',
    'leads',
    'activity',
    'marketing_dashboard',
    'automations',
    'audiences',
    'advanced_automations',
    'api_access',
    'white_label',
    'priority_support',
    'users',
    'settings',
    'client_docs_access',
    'unlimited_docs'       // Differentiator
  ],

  // Legacy/Internal mappings
  basic: [
    'dashboard',
    'clients',
    'calendar',
    'forms',
    'services',
    'financial',
    'analytics',
    'reports',
    'users',
    'settings'
  ],

  // Internal plan - FULL ACCESS to everything including beta features
  internal: [
    'dashboard',
    'clients',
    'calendar',
    'forms',              // Forms management
    'services',           // Services management
    'financial',          // Financial features
    'analytics',
    'reports',            // Reporting
    'leads',              // Lead management
    'activity',           // Activity tracking
    'marketing_dashboard',
    'automations',
    'audiences',
    'advanced_automations',
    'api_access',
    'white_label',
    'priority_support',
    'users',              // Users & Staff management
    'settings',
    'client_docs_access',
    'unlimited_docs',
    // Beta/Development features - only available to internal plan
    'beta_features',      // Access to beta feature toggles
    'debug_tools',        // Development and debugging tools
    'admin_panel',        // Administrative controls
    'feature_flags',      // Feature flag management
    'system_monitoring',  // System health and monitoring
    'data_export',        // Full data export capabilities
    'custom_integrations', // Custom integration builder
    'experimental_ui'     // Experimental UI components
  ]
};

/**
 * Check if a user has access to a specific feature
 * @param {string} feature - The feature to check access for
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasFeatureAccess = (feature) => {
  try {
    const subscriptionInfo = getSubscriptionInfo();
    const userTier = subscriptionInfo.tier || 'free';

    // Get allowed features for user's tier
    const allowedFeatures = FEATURE_ACCESS_MATRIX[userTier] || FEATURE_ACCESS_MATRIX.free;

    // Check if feature is explicitly allowed
    return allowedFeatures.includes(feature);
  } catch (error) {
    console.error('Error checking feature access:', error);
    // Default to most restrictive access on error
    return FEATURE_ACCESS_MATRIX.free.includes(feature);
  }
};

/**
 * Get all features allowed for the current user's tier
 * @returns {string[]} - Array of allowed feature names
 */
export const getAllowedFeatures = () => {
  try {
    const subscriptionInfo = getSubscriptionInfo();
    const userTier = subscriptionInfo.tier || 'free';

    return FEATURE_ACCESS_MATRIX[userTier] || FEATURE_ACCESS_MATRIX.free;
  } catch (error) {
    console.error('Error getting allowed features:', error);
    return FEATURE_ACCESS_MATRIX.free;
  }
};

/**
 * Check if current user is on a premium tier (pro or enterprise)
 * @returns {boolean} - True if user is on pro or enterprise tier
 */
export const isPremiumUser = () => {
  try {
    const subscriptionInfo = getSubscriptionInfo();
    const userTier = subscriptionInfo.tier || 'free';

    return ['pro', 'enterprise'].includes(userTier);
  } catch (error) {
    console.error('Error checking premium status:', error);
    return false;
  }
};

/**
 * Check if user is on internal plan (has access to everything including beta features)
 * @returns {boolean} True if user is on internal plan
 */
export const isInternalPlanUser = () => {
  const subscriptionInfo = getSubscriptionInfo();
  // Support both shapes just in case
  return subscriptionInfo?.tier === 'internal' || subscriptionInfo?.subscription_tier === 'internal';
};

/**
 * Check if user is on basic tier (has limited features)
 * @returns {boolean} True if user is on basic tier or lower
 */
export const isBasicTierUser = () => {
  const subscriptionInfo = getSubscriptionInfo();
  const tier = subscriptionInfo?.subscription_tier || 'free';
  return tier === 'free' || tier === 'basic';
};

/**
 * Get upgrade message for when a feature is not accessible
 * @param {string} feature - Feature name
 * @returns {string} Upgrade message
 */
export const getUpgradeMessage = (feature) => {
  const subscriptionInfo = getSubscriptionInfo();
  const currentTier = subscriptionInfo?.subscription_tier || 'free';

  // Internal plan users shouldn't see upgrade messages - they have access to everything
  if (currentTier === 'internal') {
    return `This feature is available on your internal plan. If you're seeing this message, there may be a configuration issue.`;
  }

  // Feature-specific upgrade messages
  const upgradeMessages = {
    marketing_dashboard: {
      free: "Unlock marketing insights and campaign analytics with our Pro plan!",
      basic: "Upgrade to Pro to access the marketing dashboard and track your campaign performance.",
      pro: "You have access to this feature! If you're seeing this message, please refresh the page.",
      enterprise: "You have access to this feature! If you're seeing this message, please refresh the page."
    },
    automations: {
      free: "Automate your marketing workflows with our Pro plan. Save time and increase efficiency!",
      basic: "Upgrade to Pro to access marketing automation and streamline your patient communications.",
      pro: "You have access to this feature! If you're seeing this message, please refresh the page.",
      enterprise: "You have access to this feature! If you're seeing this message, please refresh the page."
    },
    analytics: {
      free: "Get detailed insights into your practice with our Basic plan or higher.",
      basic: "You have access to this feature! If you're seeing this message, please refresh the page.",
      pro: "You have access to this feature! If you're seeing this message, please refresh the page.",
      enterprise: "You have access to this feature! If you're seeing this message, please refresh the page."
    },
    audiences: {
      free: "Create targeted patient segments with our Pro plan.",
      basic: "Upgrade to Pro to access audience segmentation and targeted marketing.",
      pro: "You have access to this feature! If you're seeing this message, please refresh the page.",
      enterprise: "You have access to this feature! If you're seeing this message, please refresh the page."
    },
    advanced_automations: {
      free: "Access advanced automation workflows with our Enterprise plan.",
      basic: "Access advanced automation workflows with our Enterprise plan.",
      pro: "Upgrade to Enterprise for advanced automation capabilities and complex workflows.",
      enterprise: "You have access to this feature! If you're seeing this message, please refresh the page."
    }
  };

  // Get specific message or fallback to generic
  const featureMessages = upgradeMessages[feature];
  if (featureMessages && featureMessages[currentTier]) {
    return featureMessages[currentTier];
  }

  // Generic upgrade messages based on current tier
  const genericMessages = {
    free: "This feature is available on our paid plans. Upgrade to unlock more capabilities!",
    basic: "This feature is available on our Pro or Enterprise plans. Upgrade for advanced functionality!",
    pro: "This feature is available on our Enterprise plan. Upgrade for the most advanced capabilities!",
    enterprise: "You should have access to this feature. Please contact support if you continue to see this message."
  };

  return genericMessages[currentTier] || "Upgrade your plan to access this feature.";
};

/**
 * Navigation menu item filter - only return menu items user has access to
 * @param {object[]} menuItems - Array of menu item objects with 'feature' property
 * @returns {object[]} - Filtered array of accessible menu items
 */
export const filterMenuItemsByAccess = (menuItems) => {
  return menuItems.filter(item => {
    // If no feature specified, always show (for basic items like logout)
    if (!item.feature) return true;

    // Check if user has access to this feature
    return hasFeatureAccess(item.feature);
  });
};

const featureAccessUtils = {
  hasFeatureAccess,
  getAllowedFeatures,
  isPremiumUser,
  isBasicTierUser,
  getUpgradeMessage,
  filterMenuItemsByAccess
};

export default featureAccessUtils; 